import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Download, ExternalLink, Loader2, FileText, Trash2 } from "lucide-react";
import { Toaster, toast } from "sonner";
import BrandHeader from "../components/BrandHeader";
import { getAudit, pdfDownloadUrl, deleteAudit } from "../lib/api";
import { sydDateTime } from "../lib/time";

const SLEEP_LABELS = { "<5": "Less than 5", "5-7": "5 to 7 hours", "7-9": "7 to 9 hours", "9+": "9 or more" };
const BROKEN_LABELS = { every_night: "Every night", every_other_night: "Every other night", few_times_week: "A few times a week", few_times_month: "A few times a month", never: "Never" };
const ENERGY = { "1-3": "1-3 I feel like a zombie", "3-5": "3-5 I'm the queen of surviving", "5-7": "5-7 Average energy", "7-9": "7-9 Above average energy", "10": "10 Energiser bunny" };
const STRESS = { "1-3": "1-3 All Panic, No Disco", "3-5": "3-5 Survival Mode", "5-7": "5-7 In and out of control", "7-9": "7-9 Feeling Zen", "10": "10 Peak Peace" };
const EMO = { daily: "Almost daily", few_times_week: "A few times a week", few_times_month: "A few times a month", never: "Never" };
const PACE = { steady: "Steady and enjoyable", balance: "Happy balance", fastest: "Fastest fat loss" };

function row(label, value) {
  return { label, value: value === 0 ? "0" : (value || "None") };
}
function buildRows(a) {
  if (!a) return [];
  return [
    row("Name", a.name),
    row("Age / Height / Weight", `${a.age} yrs · ${a.height_cm} cm · ${a.weight_kg} kg`),
    row("Average steps per day", `${(a.avg_steps || 0).toLocaleString()}${a.steps_note ? `, ${a.steps_note}` : ""}`),
    row("Workouts outside walking", `${a.workouts} (${a.workouts_per_week}/week)`),
    row("Typical bed / wake time", `${a.bed_time} → ${a.wake_time}`),
    row("Sleep routine notes", a.sleep_note),
    row("Last 2 hours before bed", a.last_two_hours),
    row("Hours of sleep most nights", SLEEP_LABELS[a.sleep_hours] || a.sleep_hours),
    row("Broken sleep frequency", BROKEN_LABELS[a.broken_sleep] || a.broken_sleep),
    row("Daily energy out of 10", ENERGY[a.energy_level] || a.energy_level),
    row("Daily stress out of 10", STRESS[a.stress_level] || a.stress_level),
    row("Eating habits & cravings", a.eating_habits),
    row("Emotional eating frequency", EMO[a.emotional_eating] || a.emotional_eating),
    row("Current daily me-time", a.me_time_current),
    row("Realistic daily commitment", a.me_time_realistic),
    row("Main goal", a.main_goal),
    row("Pace chosen", PACE[a.pace] || a.pace),
  ];
}

export default function CoachDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    getAudit(id).then(setData).catch(() => setData(null));
  }, [id]);

  if (!data) {
    return (
      <div className="min-h-screen bg-brand-base flex flex-col items-center justify-center">
        <Loader2 className="text-brand-primary animate-spin" size={32} strokeWidth={1.5} />
      </div>
    );
  }

  const rows = buildRows(data.answers);
  const pdfUrl = pdfDownloadUrl(data.id);
  const displayName = data.answers?.name || "Anonymous";

  const handleDownload = () => {
    const filename = `her-aligned-roadmap-${displayName.toLowerCase().replace(/\s+/g, "-")}.pdf`;
    const a = document.createElement("a");
    a.href = pdfUrl;
    a.download = filename;
    a.rel = "noopener";
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    setTimeout(() => document.body.removeChild(a), 1500);
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteAudit(data.id);
      toast.success(`Deleted ${displayName}.`);
      navigate("/coach", { replace: true });
    } catch (e) {
      toast.error("Couldn't delete that submission.");
      setDeleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-base">
      <Toaster richColors position="top-center" />
      <BrandHeader
        small
        right={
          <Link to="/coach" className="btn-ghost inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold" data-testid="back-coach-btn">
            <ArrowLeft size={14} strokeWidth={1.8} /> Coach view
          </Link>
        }
      />

      <main className="px-6 md:px-12 lg:px-16 pb-24 max-w-5xl mx-auto">
        <div className="label-eyebrow mt-4">Client record</div>
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mt-3">
          <h1 className="font-serif text-4xl sm:text-5xl text-brand-ink" data-testid="answers-name">
            {displayName}
          </h1>
          <div className="text-sm text-brand-ink-muted">
            Submitted {sydDateTime(data.created_at)}
          </div>
        </div>

        <div className="mt-6 flex flex-col sm:flex-row gap-3">
          <button onClick={handleDownload} className="btn-primary inline-flex items-center gap-2" data-testid="coach-download-pdf-btn">
            <Download size={16} strokeWidth={1.8} /> Download stored PDF
          </button>
          <Link to={`/results/${data.id}`} target="_blank" className="btn-ghost inline-flex items-center gap-2 border border-brand-border" data-testid="coach-view-roadmap-btn">
            <ExternalLink size={16} strokeWidth={1.8} /> Open her roadmap
          </Link>
          <button
            type="button"
            onClick={() => setConfirmOpen(true)}
            className="inline-flex items-center gap-2 border border-brand-border rounded-full px-6 py-2 text-sm uppercase tracking-[0.2em] font-bold text-brand-ink-muted hover:text-red-700 transition-colors ml-auto"
            data-testid="coach-delete-btn"
          >
            <Trash2 size={14} strokeWidth={1.8} /> Delete
          </button>
        </div>

        {/* Section: Personalised Recommendations */}
        <SectionCard title="Personalised Recommendations" testid="recs-section">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <TargetBox label="Starting Calorie Range" value={data.calorie_range} />
            <TargetBox label="Starting Protein Range" value={data.protein_range} />
            <TargetBox label="Daily Step Range" value={data.step_range} />
            <TargetBox label="Training Frequency" value={data.training_frequency} small />
          </div>
          <Paragraph label="Nutrition advice" text={data.nutrition_advice} />
          <Paragraph label="Fitness advice" text={data.fitness_advice} />
          <HabitGrid label="Sleep habits" habits={data.sleep_habits} />
          <HabitGrid label="Stress habits" habits={data.stress_habits} />
        </SectionCard>

        {/* Section: Stored PDF (inline preview) */}
        <SectionCard title="Stored PDF" testid="pdf-section">
          {data.pdf_storage_path ? (
            <div>
              <div className="text-sm text-brand-ink-muted mb-3 flex items-center gap-2">
                <FileText size={14} strokeWidth={1.6} className="text-brand-primary" />
                Stored {data.pdf_stored_at ? sydDateTime(data.pdf_stored_at) : ""} · {data.pdf_size ? `${Math.round(data.pdf_size / 1024)} KB` : ""}
              </div>
              <div className="rounded-2xl overflow-hidden border border-brand-border bg-[#FBF5EC]/40" style={{ height: 640 }}>
                <iframe src={pdfUrl} title="Stored roadmap PDF" className="w-full h-full" data-testid="pdf-iframe" />
              </div>
            </div>
          ) : (
            <p className="text-sm text-brand-ink-muted">PDF is still being generated. Refresh in a few seconds.</p>
          )}
        </SectionCard>

        {/* Section: Questionnaire Answers */}
        <SectionCard title="All Questionnaire Answers" testid="answers-section">
          <div className="rounded-2xl border border-brand-border overflow-hidden">
            <div className="divide-y divide-brand-border" data-testid="answers-table">
              {rows.map((r, i) => (
                <div key={i} className="grid grid-cols-1 md:grid-cols-12 gap-1 md:gap-6 px-6 py-5">
                  <div className="label-eyebrow md:col-span-4 md:pt-1">{r.label}</div>
                  <div className="md:col-span-8 text-brand-ink leading-relaxed whitespace-pre-wrap">{r.value}</div>
                </div>
              ))}
            </div>
          </div>
        </SectionCard>
      </main>

      {confirmOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
          role="dialog"
          aria-modal="true"
          data-testid="delete-confirm-modal"
        >
          <div className="surface-card max-w-md w-full p-6 md:p-8">
            <h3 className="font-serif text-2xl text-brand-ink">Delete this submission?</h3>
            <p className="mt-3 text-sm text-brand-ink leading-relaxed">
              You&apos;re about to permanently delete <strong>{displayName}</strong> along with the archived PDF. This cannot be undone.
            </p>
            <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-end">
              <button
                type="button"
                onClick={() => setConfirmOpen(false)}
                disabled={deleting}
                className="btn-ghost border border-brand-border rounded-full px-6 py-2 text-sm uppercase tracking-[0.2em] font-bold"
                data-testid="delete-cancel-btn"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleting}
                className="inline-flex items-center justify-center gap-2 rounded-full px-6 py-2 text-sm uppercase tracking-[0.2em] font-bold bg-red-700 text-white hover:bg-red-800 disabled:opacity-70 disabled:cursor-wait"
                data-testid="delete-confirm-btn"
              >
                {deleting ? <Loader2 size={14} strokeWidth={2} className="animate-spin" /> : <Trash2 size={14} strokeWidth={1.8} />}
                {deleting ? "Deleting…" : "Yes, delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SectionCard({ title, children, testid }) {
  return (
    <section className="mt-10 surface-card p-6 md:p-8" data-testid={testid}>
      <h2 className="font-serif text-2xl md:text-3xl text-brand-ink mb-6">{title}</h2>
      {children}
    </section>
  );
}
function TargetBox({ label, value, small = false }) {
  return (
    <div className="rounded-2xl border border-brand-border bg-[#FBF5EC]/60 p-5">
      <div className="label-eyebrow">{label}</div>
      <div className={`mt-2 font-sans font-semibold text-brand-ink ${small ? "text-base leading-snug" : "text-2xl tracking-tight"}`}>{value}</div>
    </div>
  );
}
function Paragraph({ label, text }) {
  return (
    <div className="mt-6">
      <div className="label-eyebrow">{label}</div>
      <p className="mt-2 text-brand-ink leading-relaxed whitespace-pre-line">{text}</p>
    </div>
  );
}
function HabitGrid({ label, habits }) {
  return (
    <div className="mt-6">
      <div className="label-eyebrow">{label}</div>
      <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-3">
        {(habits || []).map((h, i) => (
          <div key={i} className="rounded-xl border border-brand-border p-4 bg-white">
            <div className="font-serif text-lg text-brand-ink">{h.title}</div>
            <p className="mt-2 text-sm text-brand-ink-muted leading-relaxed">{h.explanation}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
